import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { GripVertical, ImageIcon, Pencil, Plus, Trash2, Upload } from 'lucide-react'
import { api, errorMessage } from '@/lib/api'
import type { HeroSlide } from '@/types'
import Button from '@/components/ui/Button'
import { Field, Input } from '@/components/ui/Field'
import Modal from '@/components/ui/Modal'
import PageHeader from '@/components/ui/PageHeader'
import LogoLoader from '@/components/ui/LogoLoader'
import { Table, Row, Cell, EmptyRow, actionsColumn, actionsCellClass } from '@/components/ui/Table'
import Badge from '@/components/ui/Badge'
import { useToast } from '@/components/ui/toast'

interface Draft {
  id?: number
  eyebrow: string
  title: string
  subtitle: string
  cta_label: string
  cta_url: string
  image_path: string
  image_key: string | null
  active: boolean
  sort: number
}

const emptyDraft: Draft = {
  eyebrow: '',
  title: '',
  subtitle: '',
  cta_label: '',
  cta_url: '',
  image_path: '',
  image_key: null,
  active: true,
  sort: 0,
}

export default function AdminHero() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<Draft | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<HeroSlide | null>(null)
  // Chosen file is held locally and previewed with an object URL, so nothing
  // is uploaded until Save. A cancelled edit therefore leaves no orphan file.
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)

  const slides = useQuery({
    queryKey: ['admin-hero'],
    queryFn: async () => (await api.get<{ data: HeroSlide[] }>('/admin/hero-slides')).data.data,
  })

  useEffect(() => {
    if (!file) {
      setPreview(null)
      return
    }
    const url = URL.createObjectURL(file)
    setPreview(url)
    return () => URL.revokeObjectURL(url)
  }, [file])

  const invalidate = () => {
    for (const key of ['admin-hero', 'storefront-hero']) {
      queryClient.invalidateQueries({ queryKey: [key] })
    }
  }

  const save = useMutation({
    mutationFn: async (d: Draft) => {
      let imagePath = d.image_path
      let imageKey = d.image_key

      // Upload first so the slide is only written once we have a real path.
      if (file) {
        const form = new FormData()
        form.append('image', file)
        const uploaded = (
          await api.post<{ image_path: string; image_key: string }>('/admin/hero-slides/upload', form)
        ).data
        imagePath = uploaded.image_path
        // Carries the ownership of the new file. Without it the backend cannot
        // tell this upload from a hand-typed path and would never clean it up.
        imageKey = uploaded.image_key
      }

      const payload = {
        eyebrow: d.eyebrow || null,
        title: d.title,
        subtitle: d.subtitle || null,
        cta_label: d.cta_label || null,
        cta_url: d.cta_url || null,
        image_path: imagePath,
        image_key: imageKey,
        active: d.active,
        sort: d.sort,
      }

      return d.id
        ? (await api.patch(`/admin/hero-slides/${d.id}`, payload)).data
        : (await api.post('/admin/hero-slides', payload)).data
    },
    onSuccess: (res: { message?: string }) => {
      invalidate()
      toast.success(res.message ?? 'Saved')
      setDraft(null)
      setFile(null)
    },
    onError: (err) => toast.error('Save failed', errorMessage(err)),
  })

  const remove = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/admin/hero-slides/${id}`)).data,
    onSuccess: (res: { message?: string }) => {
      invalidate()
      setConfirmDelete(null)
      toast.success(res.message ?? 'Deleted')
    },
    onError: (err) => {
      setConfirmDelete(null)
      toast.error('Delete failed', errorMessage(err))
    },
  })

  const rows = slides.data ?? []

  const openNew = () => {
    setFile(null)
    setDraft({ ...emptyDraft, sort: rows.length })
  }

  const openEdit = (slide: HeroSlide) => {
    setFile(null)
    setDraft({
      id: slide.id,
      eyebrow: slide.eyebrow ?? '',
      title: slide.title,
      subtitle: slide.subtitle ?? '',
      cta_label: slide.cta_label ?? '',
      cta_url: slide.cta_url ?? '',
      image_path: slide.image_path,
      image_key: slide.image_key ?? null,
      active: slide.active ?? true,
      sort: slide.sort ?? 0,
    })
  }

  if (slides.isLoading) return <LogoLoader />

  const shownImage = preview ?? draft?.image_path

  return (
    <div>
      <PageHeader
        title="Hero carousel"
        subtitle="Slides on the storefront home page. Lowest sort shows first; only active slides appear."
        actions={
          <Button onClick={openNew}>
            <Plus className="size-4" /> New slide
          </Button>
        }
      />

      <Table columns={['', 'Slide', 'Button', 'State', actionsColumn]}>
        {rows.length === 0 ? (
          <EmptyRow colSpan={5}>No slides yet. The storefront shows a plain banner until you add one.</EmptyRow>
        ) : (
          rows.map((slide) => (
            <Row key={slide.id} onClick={() => openEdit(slide)}>
              <Cell className="w-8 text-foreground/40">
                <GripVertical className="size-4" />
              </Cell>
              <Cell>
                <div className="flex items-center gap-3">
                  <div className="grid h-10 w-16 shrink-0 place-items-center overflow-hidden rounded border border-foreground/10 bg-foreground/5">
                    {slide.image_path ? (
                      <img
                        src={slide.image_path}
                        alt=""
                        loading="lazy"
                        className="size-full object-cover"
                        onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
                      />
                    ) : (
                      <ImageIcon className="size-4 text-foreground/30" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="truncate font-medium">{slide.title}</p>
                    {slide.eyebrow && <p className="truncate text-xs text-foreground/50">{slide.eyebrow}</p>}
                  </div>
                </div>
              </Cell>
              <Cell className="text-foreground/60">
                <p className="max-w-56 truncate">{slide.cta_label ? `${slide.cta_label} -> ${slide.cta_url}` : '-'}</p>
              </Cell>
              <Cell>
                <Badge tone={slide.active ? 'success' : 'neutral'}>{slide.active ? 'Active' : 'Hidden'}</Badge>
              </Cell>
              <Cell className={actionsCellClass}>
                <Button
                  variant="ghost"
                  className="h-8 px-2"
                  onClick={(e) => {
                    e.stopPropagation()
                    openEdit(slide)
                  }}
                >
                  <Pencil className="size-4" />
                </Button>
                <button
                  type="button"
                  title={`Delete ${slide.title}`}
                  onClick={(e) => {
                    e.stopPropagation()
                    setConfirmDelete(slide)
                  }}
                  className="-m-2 p-2 text-foreground/50 transition hover:text-danger sm:m-0 sm:ml-2 sm:p-0"
                >
                  <Trash2 className="size-4" />
                </button>
              </Cell>
            </Row>
          ))
        )}
      </Table>

      <Modal
        open={Boolean(draft)}
        onClose={() => {
          setDraft(null)
          setFile(null)
        }}
        title={draft?.id ? 'Edit slide' : 'New slide'}
        width="max-w-2xl"
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => {
                setDraft(null)
                setFile(null)
              }}
            >
              Cancel
            </Button>
            <Button
              loading={save.isPending}
              disabled={!draft?.title || (!draft?.image_path && !file)}
              onClick={() => draft && save.mutate(draft)}
            >
              Save slide
            </Button>
          </>
        }
      >
        {draft && (
          <div className="max-h-[65vh] space-y-4 overflow-y-auto pr-1 scrollbar-thin">
            {/* Live preview at the real proportions the storefront uses. */}
            <div className="relative overflow-hidden rounded-xl border border-foreground/10 bg-foreground/5">
              {shownImage ? (
                <img src={shownImage} alt="" className="aspect-[2/1] w-full object-cover" />
              ) : (
                <div className="grid aspect-[2/1] w-full place-items-center text-sm text-foreground/40">
                  No image chosen
                </div>
              )}
              <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-t from-black/80 via-black/45 to-black/30 px-4 text-center text-white">
                {draft.eyebrow && (
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/70">{draft.eyebrow}</p>
                )}
                <p className="mt-1 text-lg font-bold uppercase leading-tight drop-shadow sm:text-2xl">
                  {draft.title || 'Headline goes here'}
                </p>
                {draft.subtitle && <p className="mt-1 max-w-sm text-[11px] text-white/80">{draft.subtitle}</p>}
                {draft.cta_label && (
                  <span className="mt-3 inline-flex h-8 items-center rounded-full bg-white px-4 text-xs font-bold text-primary">
                    {draft.cta_label}
                  </span>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <label className="inline-flex h-10 cursor-pointer items-center gap-2 rounded-lg border border-primary/60 bg-primary/20 px-4 text-sm font-semibold text-primary hover:bg-primary/30">
                <Upload className="size-4" />
                {draft.image_path || file ? 'Replace image' : 'Choose image'}
                <input
                  type="file"
                  className="hidden"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />
              </label>
              <p className="text-xs text-foreground/50">
                Recommended 2560 x 1280 (2:1). Minimum 1200 x 600, max 4 MB. Other sizes are centre-cropped.
              </p>
            </div>

            <Field label="Image path" htmlFor="image_path">
              <Input
                id="image_path"
                value={draft.image_path}
                // Typing a path by hand points the slide at something we did
                // not upload, so it stops owning whatever file it had.
                onChange={(e) => setDraft({ ...draft, image_path: e.target.value, image_key: null })}
                placeholder="/products/hero1.png or upload above"
              />
            </Field>

            <Field label="Eyebrow (small label above the headline)" htmlFor="eyebrow">
              <Input
                id="eyebrow"
                value={draft.eyebrow}
                onChange={(e) => setDraft({ ...draft, eyebrow: e.target.value })}
                placeholder="MPG PRINTING MARKETPLACE"
              />
            </Field>

            <Field label="Headline" htmlFor="title">
              <Input
                id="title"
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                placeholder="Premium apparel printing for teams and businesses"
              />
            </Field>

            <Field label="Subcopy" htmlFor="subtitle">
              <Input
                id="subtitle"
                value={draft.subtitle}
                onChange={(e) => setDraft({ ...draft, subtitle: e.target.value })}
                placeholder="One supporting line under the headline"
              />
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Button label" htmlFor="cta_label">
                <Input
                  id="cta_label"
                  value={draft.cta_label}
                  onChange={(e) => setDraft({ ...draft, cta_label: e.target.value })}
                  placeholder="Browse products"
                />
              </Field>
              <Field label="Button links to" htmlFor="cta_url">
                <Input
                  id="cta_url"
                  value={draft.cta_url}
                  onChange={(e) => setDraft({ ...draft, cta_url: e.target.value })}
                  placeholder="/products"
                />
              </Field>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Sort (lowest shows first)" htmlFor="sort">
                <Input
                  id="sort"
                  type="number"
                  min={0}
                  value={draft.sort}
                  onChange={(e) => setDraft({ ...draft, sort: Number(e.target.value) })}
                />
              </Field>
              <Field label="Visibility">
                <label className="inline-flex h-10 cursor-pointer items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={draft.active}
                    onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
                    className="size-4 rounded border-foreground/30"
                  />
                  Show this slide on the storefront
                </label>
              </Field>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        open={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
        title="Delete slide?"
        footer={
          <>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              onClick={() => confirmDelete && remove.mutate(confirmDelete.id)}
            >
              Delete slide
            </Button>
          </>
        }
      >
        <p className="text-sm text-foreground/70">
          "{confirmDelete?.title}" is removed from the carousel, and its uploaded image is deleted. This cannot be
          undone.
        </p>
      </Modal>
    </div>
  )
}
